import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import 'react-datepicker/dist/react-datepicker.css'
import classNames from 'classnames'

import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../common/errorMessages'
import { IntegrationServiceId } from '../../../../common/integrations'

import { useExportConfirmation } from '../../../../components/ConfirmationDialog/useExportConfirmation'
import { SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { VerticalDivider } from '../../../../components/Divider/Divider'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { Button } from '../../../../components/Button/Button'
import { Modal } from '../../../../components/Modal/Modal'

import { ReactComponent as MergeIcon } from '../../../../assets/images/merge-icon.svg'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../models/Account'
import { ResolvedIdentity, Task } from '../../../../models/MeetingSupport'
import { ExternalUserServiceIntegration, User } from '../../../../models/User'
import { IntegrationEntities } from '../../../../models/Integration'
import { AiResult } from '../../../../models/AiResult'

import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { useLazyGetAllTasksQuery } from '../../../../store/meetingSupport/meetingSupport'
import { selectTasks, setTasks, setTasksLoading } from '../../../../store/task/task'
import { selectRoomSession } from '../../../../store/meetingSummary/roomSession'
import { selectCurrentUser } from '../../../../store/meetingSummary/user'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  useGetIntegrationTeammatesQuery,
  useGetIntegrationUsersQuery,
  useLazyGetIntegrationEntitiesQuery,
  useLazyGetIntegrationUsersQuery,
  useSaveTasksMutation,
} from '../../../../store/integrations/integrations'

import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { CRMModalProps } from '../../../../apps/professional-app/fa/MeetingSummary/CRM/models'

import { TaskDueDateOffset } from '../../../Integration/IntegrationManagement/IntegrationSpecificSettings/CRMIntegrationTaskSettings'

import { getContactOptions } from '../utils'

import { TaskSourceType, useTaskMutations } from '../../../Tasks/hooks'
import { TasksTable } from './Task/TasksTable'
import { EditMode, TaskContextDetails, TaskEditSection } from './Task/TaskEditSection'

import './AddToCRMModal.scss'
import { taskCategoryEntityType } from './Task/utils'
import { RoomSession } from '../../../../models/Room'
import { useGetAllUsersQuery } from '../../../../store/user/user'
import { useSyncStatuses } from '../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/hooks'
import { IntegrationSavedModalBody } from '../../../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { getDefaultIntegrationSettingsFormValues } from '../../../Integration/utils'
import { ContactDropdown } from '../ContactDropdown'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

export type FormTask = {
  id: string
  dueDate: Date
  task: string
  description?: string
  priority: string
  status: string
  assignedTo?: string | null
  multipleAssignedTo: string[]
  selected: boolean
  category: string | null
  zocksAssignedTo?: ResolvedIdentity
}

const SMARTOFFICE_TASK_TYPES = [
  { id: '1', name: 'Appointment' },
  { id: '2', name: 'Call' },
  { id: '3', name: 'Todo' },
  { id: '4', name: 'Project' },
  { id: '5', name: 'Non-work' },
  { id: '6', name: 'Meeting' },
  { id: '7', name: 'Email' },
]

export type FormValues = {
  externalUserId: string
  tasks: FormTask[]
}

const EDITABLE_TASK_SOURCE_TYPES = [TaskSourceType.MEETING, TaskSourceType.EMAIL]

const zocksTaskToFormTask = (task: Task, dueDate: Date, assignedTo: string | null): FormTask => {
  return {
    id: task.id,
    task: task.task,
    description: task.description || '',
    status: 'open',
    zocksAssignedTo: task.assignedTo,
    priority: 'Medium',
    selected: false,
    category: null,
    dueDate,
    assignedTo,
    multipleAssignedTo: assignedTo ? [assignedTo] : [],
  }
}

const formTaskToSaveTaskPayload = (formTask: FormTask) => {
  const { id, task, description, status, priority, assignedTo, multipleAssignedTo, dueDate: _dueDate, category, zocksAssignedTo } = formTask
  const dueDate = _dueDate.toISOString()
  return {
    taskId: id,
    task,
    status,
    dueDate,
    priority,
    assignedTo,
    multipleAssignedTo,
    zocksAssignedTo,
    ...(category && { category }),
    ...(description && { description }),
  }
}

export const AddMeetingTaskToCRMModal: React.FC<CRMModalProps<AiResult>> = ({ show, onHide, result, serviceId }) => {
  const dispatch = useAppDispatch()

  const { roomId, sessionId } = result

  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectCurrentUser)
  const roomSession = useAppSelector(selectRoomSession)
  const tasks = useAppSelector(selectTasks)

  const [triggerGetTasks] = useLazyGetAllTasksQuery()

  const userIntegration = useMemo(() => currentUser?.integrations?.find((e) => e.serviceId === serviceId), [currentUser, serviceId])

  const refetchTasks = async () => {
    dispatch(setTasksLoading(true))
    try {
      const filters = { type: 'meeting', roomId, sessionId }
      const { data: tasks } = await triggerGetTasks({ accountId, filters }).unwrap()
      dispatch(setTasks(tasks))
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setTasksLoading(false))
    }
  }

  const handleOnHide = async (isSaved?: boolean) => {
    if (isSaved) {
      await refetchTasks()
    }
    onHide()
  }

  if (!roomSession || !tasks || !userIntegration) {
    return null
  }

  return (
    <AddTaskToCRMModal
      show={show}
      onHide={handleOnHide}
      tasks={tasks}
      userIntegration={userIntegration}
      baseTaskDueDate={roomSession.finishedAt ? new Date(roomSession.finishedAt) : null}
      taskContextDetails={{ roomId, sessionId }}
      type={TaskSourceType.MEETING}
      roomSession={roomSession}
    />
  )
}

interface AddTaskToCRMModalProps {
  show: boolean
  onHide: (isSaved?: boolean) => void
  userIntegration: ExternalUserServiceIntegration
  tasks: Task[]
  baseTaskDueDate?: Date | null
  taskContextDetails: TaskContextDetails
  type: TaskSourceType
  roomSession?: RoomSession | null
  onSave?: () => void
}

export const AddTaskToCRMModal: React.FC<AddTaskToCRMModalProps> = ({
  show,
  onHide,
  userIntegration,
  tasks,
  baseTaskDueDate,
  taskContextDetails,
  type,
  roomSession,
  onSave,
}) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)

  const [triggerSaveTasks, { isLoading }] = useSaveTasksMutation()

  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const [triggerGetTasks, { isLoading: isGetTasksResultLoading }] = useLazyGetAllTasksQuery()

  const { data: users, isLoading: isUsersLoading } = useGetAllUsersQuery({
    accountId: accountId,
    identityTypes: ['idp'],
  })

  const { data: teammatesData, isLoading: isTeammatesLoading } = useGetIntegrationTeammatesQuery({
    accountId,
    connector: userIntegration.serviceId,
  })

  const { tracked } = useTracking()
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation({ roomSession })

  const defaultValues: FormValues = { externalUserId: '', tasks: [] }
  const form = useForm<FormValues>({ defaultValues, mode: 'onChange', reValidateMode: 'onChange' })

  const [syncedDate, setSyncedDate] = useState<Date | null>(null)
  const [savedTaskResult, setSavedTaskResult] = useState<boolean>(false)
  const { taskSyncStatuses } = useSyncStatuses()

  const taskDueDate = useMemo(() => {
    const { serviceConfig, serviceId } = userIntegration
    const taskDueDateOffset = serviceConfig?.taskDueDateOffset ?? getDefaultIntegrationSettingsFormValues(serviceId).taskDueDateOffset
    const { value, unit } = taskDueDateOffset as TaskDueDateOffset
    const date = baseTaskDueDate ? new Date(baseTaskDueDate) : new Date()

    if (unit === 'days') {
      date.setDate(date.getDate() + value)
    } else if (unit === 'weeks') {
      date.setDate(date.getDate() + value * 7)
    } else if (unit === 'months') {
      date.setMonth(date.getMonth() + value)
    }

    return date
  }, [baseTaskDueDate, userIntegration])

  useEffect(() => {
    if (!taskDueDate || isTeammatesLoading || isUsersLoading) {
      return
    }

    const getDefaultAssigneeForTask = (task: Task): string => {
      if (!accountId || !task.assignedTo?.user?.id) {
        return userIntegration.externalId
      }

      const user = users?.data?.find((u) => u.id === task.assignedTo!.user!.id)

      if (!user || !teammatesData) {
        return userIntegration.externalId
      }

      const userIntegrationForService = user.integrations?.find((i) => i.serviceId === userIntegration.serviceId)

      if (!userIntegrationForService?.externalId) {
        return userIntegration.externalId
      }

      const isInTeammates = teammatesData?.data?.some((teammate) =>
        teammate.integrations?.some((ti) => ti.serviceId === userIntegration.serviceId && ti.externalId === userIntegrationForService.externalId),
      )

      return isInTeammates ? userIntegrationForService.externalId : userIntegration.externalId
    }

    const existingFormTasks = form.getValues('tasks')

    const syncedFormTasks = tasks
      .filter((task) => !task.syncStatus.length)
      .map((task) => {
        const existingTask = existingFormTasks.find((existingFormTask) => existingFormTask.id === task.id)

        if (existingTask) {
          return {
            ...existingTask,
            task: task.task,
            description: task.description,
          }
        }

        const defaultAssignee = getDefaultAssigneeForTask(task)
        return zocksTaskToFormTask(task, taskDueDate, defaultAssignee)
      })

    if (JSON.stringify(existingFormTasks) !== JSON.stringify(syncedFormTasks)) {
      form.setValue('tasks', syncedFormTasks, { shouldDirty: true })
    }
  }, [form, taskDueDate, tasks, userIntegration, teammatesData, isTeammatesLoading, users, isUsersLoading, accountId])

  const handleSelectAll = (checked: boolean) => {
    const formTasks = form.getValues('tasks').map((task) => ({ ...task, selected: checked }))
    form.setValue('tasks', formTasks)
  }

  const handleHide = () => {
    form.reset()
    onHide()
  }

  const _handleSave = async (data: FormValues) => {
    const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, userIntegration.serviceId)
    const contactMapping =
      typeof serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
        ? null
        : serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT]?.objectName || 'contact'

    const { tasks: formTasks, externalUserId } = data

    const selectedTasks = formTasks.filter((task) => task.selected)
    const payload = {
      relatedTo: { id: externalUserId, type: contactMapping },
      tasks: selectedTasks.map(formTaskToSaveTaskPayload),
    }

    const saveTask = async () => {
      const syncedDateTemp = new Date()
      try {
        const response = await triggerSaveTasks({
          accountId,
          connector: userIntegration.serviceId,
          body: payload,
        })
        dispatch(
          addToast({
            variant: 'success',
            title: 'Results saved',
            messages: [`Task(s) were successfully saved to your CRM`],
          }),
        )
        if (!response.error && roomSession) {
          const filters = { type: 'meeting', roomId: roomSession.roomId, sessionId: roomSession.id }
          const existingTasks = await triggerGetTasks(
            {
              accountId,
              filters,
            },
            false,
          ).unwrap()
          if (existingTasks) {
            setSyncedDate(syncedDateTemp)
            setSavedTaskResult(true)
            dispatch(setTasks(existingTasks.data))
          }
        }
      } catch (e) {
        console.error(e)
      }
    }

    await runMutationWithConfirmation(saveTask)
  }

  const handleSave = tracked(
    _handleSave,
    { eventName: TrackEvent.SAVE_TASKS_TO_CRM },
    {
      eventDetailsExtractor: (data: FormValues) => ({
        ...taskContextDetails,
        externalUserId: data.externalUserId,
        connector: userIntegration.serviceId,
        multipleAssignedTo: !!data.tasks.filter((t) => t.multipleAssignedTo.length > 1).map((t) => t.id).length,
      }),
    },
  )

  const handleOnSaveToCRM = async () => {
    await form.handleSubmit(handleSave)()
    if (onSave) {
      onSave()
    }
  }

  const header = !savedTaskResult ? (
    <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-start'>
      Save Tasks to&nbsp;<span className='text-capitalize'>{userIntegration.serviceId}</span>
    </BootstrapModal.Title>
  ) : null

  const body = (
    <FormProvider {...form}>
      {savedTaskResult && taskSyncStatuses.length ? (
        <IntegrationSavedModalBody syncStatuses={taskSyncStatuses} serviceId={userIntegration.serviceId} onHide={handleHide} syncTime={syncedDate} />
      ) : (
        <AddTaskToCRMModalBody
          onSelectAll={handleSelectAll}
          serviceId={userIntegration.serviceId}
          onSaveToCRM={handleOnSaveToCRM}
          isSaveToCRMLoading={isLoading}
          taskContextDetails={taskContextDetails}
          type={type}
          teammatesData={teammatesData?.data ?? null}
          isFetching={isTeammatesLoading || isUsersLoading}
        />
      )}
    </FormProvider>
  )

  return (
    <>
      <Modal
        show={show}
        onHide={handleHide}
        header={header}
        body={body}
        bodyClassName={'p-0'}
        showFooterActions={false}
        footerPrimaryActionLoading={isLoading || accountIsLoading || isGetTasksResultLoading}
        onFooterSecondaryAction={handleHide}
        centered={false}
        size='xl'
        className={'add-task-to-crm-modal'}
      />
      <ConfirmationModal />
    </>
  )
}

interface AddTaskToCRMModalBodyProps {
  onSelectAll: (checked: boolean) => void
  serviceId: IntegrationServiceId
  onSaveToCRM: () => void
  isSaveToCRMLoading: boolean
  taskContextDetails: TaskContextDetails
  type: TaskSourceType
  teammatesData?: User[] | null
  isFetching?: boolean
}

const getTaskOrTasks = (count: number): string => {
  return count === 1 ? 'task' : 'tasks'
}

const getTaskLabel = (taskCount: number, selectedCount: number): string => {
  if (selectedCount === 0) {
    return `${taskCount} ${getTaskOrTasks(taskCount)}`
  } else {
    return `${selectedCount} ${getTaskOrTasks(selectedCount)} selected`
  }
}
const AddTaskToCRMModalBody: React.FC<AddTaskToCRMModalBodyProps> = ({
  onSelectAll,
  serviceId,
  onSaveToCRM,
  isSaveToCRMLoading,
  taskContextDetails,
  type,
  teammatesData,
  isFetching,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { tasks, deleteTask, isDeletingTask } = useTaskMutations()
  const { formState, watch, getValues } = useFormContext<FormValues>()
  const formTasks = watch('tasks')

  const [editMode, setEditMode] = useState<EditMode | null>(null)
  const [tasksToMerge, setTasksToMerge] = useState<FormTask[]>([])
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [asyncCategoryOptions, setAsyncCategoryOptions] = useState<SelectOption[]>([])

  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId })

  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()
  const savedScrollPositionRef = useRef<number | null>(null)
  const [searchedContacts, setSearchedContacts] = useState<SelectOption[]>([])
  const [triggerGetIntegrationEntities] = useLazyGetIntegrationEntitiesQuery()

  const getAssignedToOptions = useCallback(
    (users: User[]) => {
      return users.map((user) => {
        const contactIntegration = user.integrations?.find((i) => i.serviceId === serviceId)
        const contactLabel = `${getUserDisplayName(user)}${user.email ? ` (${user.email})` : ''}`
        return { id: contactIntegration!.externalId, label: contactLabel }
      })
    },
    [serviceId],
  )

  const getTaskCategoryOptions = useCallback(
    (integrationEntities: IntegrationEntities | null = null) => {
      if (serviceId === 'smartoffice') {
        const options = SMARTOFFICE_TASK_TYPES.map(({ id, name }) => ({ id, label: name }))
        return [{ id: null, label: 'No category' }, ...options]
      }
      if (!integrationEntities) {
        return [{ id: null, label: 'No category' }]
      }
      const taskCategories = integrationEntities[taskCategoryEntityType[serviceId].dto] ?? []
      const options = taskCategories.map(({ id, name }: { id: string; name: string }) => ({ id, label: name }))
      return [{ id: null, label: 'No category' }, ...options]
    },
    [serviceId],
  )

  const onSearchInputChange = useCallback(
    async (searchInput: string) => {
      if (!searchInput) {
        setSearchedContacts([])
        return
      }

      try {
        const contactsData = await triggerGetContacts({
          accountId,
          connector: serviceId,
          filters: { name: searchInput },
        }).unwrap()

        if (contactsData?.data) {
          setSearchedContacts(getContactOptions(contactsData.data, serviceId))
        }
      } catch (e) {
        console.error('Search failed:', e)
      }
    },
    [accountId, serviceId, triggerGetContacts],
  )

  const assignedToOptions = useMemo(() => {
    if (teammatesData) {
      return getAssignedToOptions(teammatesData)
    }
    return []
  }, [teammatesData, getAssignedToOptions])

  const taskCategoryOptions = useMemo(() => {
    if (!Object.keys(taskCategoryEntityType).includes(serviceId)) {
      return []
    }

    if (serviceId === 'smartoffice') {
      return getTaskCategoryOptions()
    }

    return asyncCategoryOptions
  }, [serviceId, getTaskCategoryOptions, asyncCategoryOptions])

  useEffect(() => {
    const isSupported = Object.keys(taskCategoryEntityType).includes(serviceId)
    if (serviceId === 'smartoffice' || !isSupported) {
      return
    }

    triggerGetIntegrationEntities({
      accountId,
      connector: serviceId,
      entityType: taskCategoryEntityType[serviceId].api,
    })
      .unwrap()
      .then((r) => {
        if (r?.data) {
          setAsyncCategoryOptions(getTaskCategoryOptions(r.data))
        }
      })
      .catch(console.error)
  }, [accountId, serviceId, triggerGetIntegrationEntities, getTaskCategoryOptions])

  const contactOptions = useMemo(() => {
    const initialOptions = contacts?.data ? getContactOptions(contacts.data, serviceId) : []
    if (searchedContacts.length > 0) {
      const combined = [...searchedContacts, ...initialOptions]
      return combined.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
    }

    return initialOptions
  }, [contacts, searchedContacts, serviceId])

  useEffect(() => {
    if (editMode === null && savedScrollPositionRef.current !== null) {
      if (scrollContainerRef.current) {
        if (savedScrollPositionRef.current != null) {
          scrollContainerRef.current.scrollTop = savedScrollPositionRef.current
        }
        savedScrollPositionRef.current = null
      }
    }
  }, [editMode])

  const handleAddNewTask = useCallback(() => {
    setEditMode('add')
  }, [])

  const handleMergeTasks = useCallback(() => {
    const selected = getValues('tasks').filter((t) => t.selected)
    setTasksToMerge(selected)
    setEditMode('merge')
  }, [getValues])

  const handleEditTask = useCallback(
    (taskId: string) => {
      if (scrollContainerRef.current) {
        savedScrollPositionRef.current = scrollContainerRef.current.scrollTop
      }
      const taskToEdit = tasks.find((t) => t.id === taskId)
      if (taskToEdit) {
        setTaskToEdit(taskToEdit)
        setEditMode('edit')
      }
    },
    [tasks],
  )

  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      const taskToDelete = tasks.find((t) => t.id === taskId)
      if (!taskToDelete) {
        return
      }

      try {
        await deleteTask(taskToDelete)

        dispatch(
          addToast({
            variant: 'success',
            title: 'Success',
            messages: ['Task successfully deleted.'],
          }),
        )
      } catch (error) {
        console.error('Failed to delete task:', error)
        dispatch(
          addToast({
            variant: 'error',
            messages: ['Failed to delete task. Please try again.'],
          }),
        )
      }
    },
    [tasks, deleteTask, dispatch],
  )

  const handleOnEditCancelled = useCallback(() => {
    setEditMode(null)
    setTasksToMerge([])
    setTaskToEdit(null)
  }, [])

  const handleOnEditSaved = useCallback(async () => {
    setEditMode(null)
    setTasksToMerge([])
    setTaskToEdit(null)
  }, [])

  if ((isContactsFetching && !contacts) || isFetching) {
    return (
      <div className='d-flex justify-content-center p-4'>
        <Spinner />
      </div>
    )
  }

  if (
    (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(contactsError).match(ignoredMessage))) ||
    (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage)))
  ) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
      </>
    )
  }
  const isAllSelected = formTasks.length > 0 && formTasks.every((t) => t.selected)
  const selectedCount = formTasks.filter((t) => t.selected).length
  const anySelected = selectedCount > 0
  const taskLabel = getTaskLabel(formTasks.length, selectedCount)

  return (
    <Container fluid className='d-flex flex-column' style={{ maxHeight: 'calc(96vh - 120px)' }}>
      <Row className='zocks-bg-color-gray-100 zocks-border-color-gray-300 border-top border-bottom py-2 px-3 d-flex align-items-center flex-shrink-0'>
        <Col lg={true}>
          <span className={classNames(anySelected ? 'lexical-text-bold' : '')}>{!editMode && taskLabel}</span>
        </Col>
        <Col lg='auto'>
          {EDITABLE_TASK_SOURCE_TYPES.includes(type) ? (
            <Button variant='header' className='px-2 py-1 button-large' onClick={handleAddNewTask} disabled={!!editMode || isDeletingTask}>
              <i className='bi bi-plus-lg' />
              Add
            </Button>
          ) : null}
        </Col>
        <Col lg='auto'>
          {EDITABLE_TASK_SOURCE_TYPES.includes(type) ? (
            <Button variant='header' className='px-2 py-1 button-large' disabled={!!editMode || selectedCount < 2 || isDeletingTask} onClick={handleMergeTasks}>
              <MergeIcon />
              Merge
            </Button>
          ) : null}
        </Col>
        <Col lg='auto' className='align-self-stretch d-flex'>
          <VerticalDivider />
        </Col>
        <Col lg={4}>
          <ContactDropdown
            showLabel={false}
            fieldName='externalUserId'
            contactOptions={contactOptions}
            serviceId={serviceId}
            onSearchInputChange={onSearchInputChange}
          />
        </Col>
        <Col lg='auto'>
          <Button
            variant='primary'
            className='px-3 py-2 button-large'
            disabled={!!editMode || selectedCount === 0 || isDeletingTask}
            loading={isSaveToCRMLoading}
            onClick={onSaveToCRM}
          >
            <IntegrationIcon
              className='crm-icon'
              serviceId={serviceId as IntegrationServiceId}
              fill={selectedCount === 0 || !!editMode ? '#adb5bd' : '#FFFFFF'}
            />
            <span>
              Save to <span className='text-capitalize'>{serviceId}</span>
            </span>
          </Button>
        </Col>
      </Row>
      <div ref={scrollContainerRef} className='flex-grow-1' style={{ overflowY: 'auto', overflowX: 'hidden' }}>
        {editMode && (
          <TaskEditSection
            mode={editMode}
            onEditSaved={handleOnEditSaved}
            onEditCancelled={handleOnEditCancelled}
            taskToEdit={taskToEdit}
            tasksToMerge={tasksToMerge}
            assignedToOptions={assignedToOptions}
            taskCategoryOptions={taskCategoryOptions}
            type={type}
            taskContextDetails={taskContextDetails}
          />
        )}

        {!editMode && (
          <Row className='mt-1'>
            <Col>
              <TasksTable
                serviceId={serviceId}
                isAllSelected={isAllSelected}
                anySelected={anySelected}
                onSelectAll={onSelectAll}
                tasks={formTasks}
                taskCategoryOptions={taskCategoryOptions}
                assignedToOptions={assignedToOptions}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                editDisabled={!EDITABLE_TASK_SOURCE_TYPES.includes(type)}
              />
            </Col>
          </Row>
        )}
      </div>
      {formState.errors?.tasks?.root ? (
        <Row>
          <Col>
            <FormFeedback>{formState.errors?.tasks?.root.message}</FormFeedback>
          </Col>
        </Row>
      ) : null}
    </Container>
  )
}
